import type { DatabaseRecord, Enduser, FormResponseValue } from "@tellescope/types-models";
import { SupportedSession, TellescopeModelFromName } from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import type { NextFieldLogicOptions } from "../utils/types";
import type { FormTranslationMap } from "../utils";
import type { FormTypesWithValues, TellescopeFieldResponse } from "../types";
import { TellescopeFormField } from "../tellescope-form-field";
import type { QuestionGroupFieldProps } from "./QuestionGroupField";

export interface QuestionGroupSubFieldProps {
  subField: TellescopeModelFromName<"form_fields">;
  convertedType: Exclude<FormTypesWithValues, "Question Group" | "Redirect">;
  getFieldProps: QuestionGroupFieldProps["getFieldProps"];
  responsesArray: TellescopeFieldResponse[];
  logicOptions: NextFieldLogicOptions;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  session?: SupportedSession | null;
  form?: TellescopeModelFromName<"forms">;
  formResponses?: FormResponseValue[];
  enduserId?: string;
  organizationSubdomain?: string;
  applyDatabasePrepopulation?: (records: DatabaseRecord[]) => void;
  setCustomerId?: (customerId: string) => void;
  setBookedAppointmentUrl?: (bookedAppointmentUrl: string | undefined) => void;
  formResponseId?: string;
  goToPreviousField?: () => void;
  isPreviousDisabled?: boolean;
  translations?: FormTranslationMap;
}

export const QuestionGroupSubField = ({
  subField,
  convertedType,
  getFieldProps,
  responsesArray,
  logicOptions,
  enduser,
  session,
  form,
  formResponses,
  enduserId,
  organizationSubdomain,
  applyDatabasePrepopulation,
  setCustomerId,
  setBookedAppointmentUrl,
  formResponseId,
  goToPreviousField,
  isPreviousDisabled,
  translations,
}: QuestionGroupSubFieldProps) => {
  const subFieldId = subField.id;
  const placeholder = subField.placeholder;

  switch (convertedType) {
    case "Appointment Booking": {
      if (!session || !enduserId) return null;
      const props = getFieldProps(subFieldId, "Appointment Booking");
      if (!props) return null;
      return (
        <TellescopeFormField<"Appointment Booking">
          {...props}
          placeholder={placeholder}
          session={session}
          enduserId={enduserId}
          organizationSubdomain={organizationSubdomain}
          priorResponses={responsesArray}
          setBookedAppointmentUrl={setBookedAppointmentUrl}
          formResponseId={formResponseId}
          logic={logicOptions.urlLogicValue}
          formId={subField.formId}
          goToPreviousField={goToPreviousField}
          isPreviousDisabled={isPreviousDisabled}
        />
      );
    }
    case "Stripe": {
      if (!form || !enduserId) return null;
      const props = getFieldProps(subFieldId, "Stripe");
      if (!props) return null;
      return (
        <TellescopeFormField<"Stripe">
          {...props}
          placeholder={placeholder}
          enduserId={enduserId}
          form={form}
          formResponses={formResponses ?? responsesArray}
          setCustomerId={setCustomerId}
        />
      );
    }
    case "Chargebee": {
      if (!session || !enduserId) return null;
      const props = getFieldProps(subFieldId, "Chargebee");
      if (!props) return null;
      return (
        <TellescopeFormField<"Chargebee">
          {...props}
          placeholder={placeholder}
          session={session}
          enduserId={enduserId}
          formResponses={formResponses ?? responsesArray}
          isOptional={subField.isOptional}
          setCustomerId={setCustomerId}
        />
      );
    }
    case "Database Select": {
      const props = getFieldProps(subFieldId, "Database Select");
      if (!props) return null;
      return (
        <TellescopeFormField<"Database Select">
          {...props}
          placeholder={placeholder}
          formResponses={formResponses ?? responsesArray}
          enduser={enduser}
          onDatabaseSelect={applyDatabasePrepopulation}
        />
      );
    }
    case "Insurance": {
      const props = getFieldProps(subFieldId, "Insurance");
      if (!props) return null;
      return (
        <TellescopeFormField<"Insurance">
          {...props}
          placeholder={placeholder}
          formResponses={formResponses ?? responsesArray}
          enduser={enduser}
          onDatabaseSelect={applyDatabasePrepopulation}
        />
      );
    }
    case "signature": {
      const props = getFieldProps(subFieldId, "signature");
      if (!props) return null;
      return (
        <TellescopeFormField<"signature">
          {...props}
          placeholder={placeholder}
          fullName={enduser ? formatName(enduser) : ""}
        />
      );
    }
    case "multiple_choice": {
      const props = getFieldProps(subFieldId, "multiple_choice");
      if (!props) return null;
      return (
        <TellescopeFormField<"multiple_choice">
          {...props}
          placeholder={placeholder}
          checkboxList
          formResponses={formResponses ?? responsesArray}
          logicOptions={logicOptions}
          translations={translations}
        />
      );
    }
    case "Pharmacy Search": {
      const props = getFieldProps(subFieldId, "Pharmacy Search");
      if (!props) return null;
      return (
        <TellescopeFormField<"Pharmacy Search">
          {...props}
          placeholder={placeholder}
          enduser={enduser}
          formResponses={responsesArray}
        />
      );
    }
    case "Bridge Eligibility": {
      if (!enduserId) return null;
      const props = getFieldProps(subFieldId, "Bridge Eligibility");
      if (!props) return null;
      return (
        <TellescopeFormField<"Bridge Eligibility">
          {...props}
          placeholder={placeholder}
          enduserId={enduserId}
          enduser={enduser}
          priorResponses={responsesArray}
          sessionType="enduser"
        />
      );
    }
    case "Candid Eligibility": {
      if (!enduserId) return null;
      const props = getFieldProps(subFieldId, "Candid Eligibility");
      if (!props) return null;
      return (
        <TellescopeFormField<"Candid Eligibility">
          {...props}
          placeholder={placeholder}
          enduserId={enduserId}
          enduser={enduser}
          priorResponses={responsesArray}
          sessionType="enduser"
        />
      );
    }
    case "Stedi Eligibility": {
      if (!enduserId) return null;
      const props = getFieldProps(subFieldId, "Stedi Eligibility");
      if (!props) return null;
      return (
        <TellescopeFormField<"Stedi Eligibility">
          {...props}
          placeholder={placeholder}
          enduserId={enduserId}
          enduser={enduser}
          priorResponses={responsesArray}
          sessionType="enduser"
          formResponseId={formResponseId}
        />
      );
    }
    case "Dropdown": {
      const props = getFieldProps(subFieldId, "Dropdown");
      if (!props) return null;
      return (
        <TellescopeFormField<"Dropdown">
          {...props}
          placeholder={placeholder}
          formResponses={formResponses ?? responsesArray}
          logicOptions={logicOptions}
          translations={translations}
        />
      );
    }
    default: {
      const limitedType: Exclude<
        FormTypesWithValues,
        | "Appointment Booking"
        | "Stripe"
        | "Chargebee"
        | "Pharmacy Search"
        | "Bridge Eligibility"
        | "Candid Eligibility"
        | "Stedi Eligibility"
      > = convertedType;
      const props = getFieldProps<typeof limitedType>(subFieldId, limitedType);
      if (!props) return null;
      const subFieldProps = { ...props, placeholder, enduserId, translations };
      // @ts-expect-error Union type has been reduced, and TS doesn't like it
      return <TellescopeFormField<typeof limitedType> {...subFieldProps} />;
    }
  }
};
